import { Link, useLocation, Outlet } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Send,
  Calendar,
  FileText,
  Grid3x3,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useState } from 'react'

const appItems = [
  { icon: Send, label: '发布', path: '/dashboard/publish' },
  { icon: Calendar, label: '计划', path: '/dashboard/schedule', badge: 'Beta' },
  { icon: FileText, label: 'Markdown', path: '/dashboard/md' },
  { icon: Grid3x3, label: '九宫格', path: '/dashboard/grid' },
]

const bottomItems = [
  { icon: Settings, label: '设置', path: '/dashboard/settings' },
]

export default function DashboardLayout() {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="min-h-screen">
      {/* Sidebar - Fixed on left */}
      <motion.aside
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className={`fixed left-0 top-0 z-40 h-screen border-r border-border/50 bg-card/50 backdrop-blur-xl transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-56'
        }`}
      >
        <div className="flex h-full flex-col overflow-y-auto px-3 py-4">
          {/* Apps */}
          <div className="space-y-1">
            {!collapsed && (
              <p className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                应用
              </p>
            )}
            {appItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname.startsWith(item.path)
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`relative flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="sidebar-active"
                      className="absolute inset-0 rounded-lg bg-secondary"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 flex items-center">
                    <Icon className={`${collapsed ? '' : 'mr-3'} size-4`} />
                    {!collapsed && (
                      <>
                        {item.label}
                        {item.badge && (
                          <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                </Link>
              )
            })}
          </div>

          {/* Bottom */}
          <div className="mt-auto space-y-1">
            {!collapsed && bottomItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`relative flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="sidebar-active"
                      className="absolute inset-0 rounded-lg bg-secondary"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 flex items-center">
                    <Icon className={`${collapsed ? '' : 'mr-3'} size-4`} />
                    {!collapsed && item.label}
                  </span>
                </Link>
              )
            })}
          </div>

          {/* Collapse Button */}
          <motion.button
            onClick={() => setCollapsed(!collapsed)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="mt-4 flex items-center justify-center rounded-lg border border-border/50 p-2 hover:bg-secondary/50 transition-colors"
          >
            {collapsed ? (
              <ChevronRight className="size-4" />
            ) : (
              <>
                <ChevronLeft className="size-4" />
                <span className="ml-2 text-sm">收起</span>
              </>
            )}
          </motion.button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div
        className="transition-all duration-300"
        style={{ marginLeft: collapsed ? '4rem' : '14rem' }}
      >
        <main className="px-8 py-6 min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
